import { useEffect, useMemo, useState } from "react";
import Card from "components/card";
import Loader from "components/loader/Loader";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import PaginationControls from "components/paginationControls/PaginationControls";
import { Box, Icon, Text } from "@chakra-ui/react";
import { FiInbox } from "react-icons/fi";
import { FaShoppingCart, FaTools, FaUndoAlt } from "react-icons/fa";
import { FaCashRegister } from "react-icons/fa6";
import moment from "moment";
import 'moment/locale/es';
import { currencyFormat } from "utils/Funciones";

const columnHelper = createColumnHelper();

export default function TablePaginationByProdId({
  searchTerm,
  tableData,
  fetchData,
  loading,
  pageCount,
  totalRow
}) {
  const [sorting, setSorting] = useState([]);

  const columns = useMemo(() => [
    columnHelper.accessor("tipo", {
      header: () => <span className="font-bold">Movimiento</span>,
      cell: (info) => {
        const tipo = info.getValue();
        let config = {
          label: tipo,
          icon: null,
          gradient: "linear(to-r, gray.200, gray.400)",
          textColor: "gray.800",
        };

        switch (tipo) {
          case "venta":
            config = {
              label: "Venta",
              icon: <FaCashRegister />,
              gradient: "linear(to-r, blue.400, blue.600)",
              textColor: "white",
            };
            break;
          case "compra":
            config = {
              label: "Compra",
              icon: <FaShoppingCart />,
              gradient: "linear(to-r, green.400, green.600)",
              textColor: "white",
            };
            break;
          case "devolucion":
            config = {
              label: "Devolución",
              icon: <FaUndoAlt />,
              gradient: "linear(to-r, purple.400, purple.600)",
              textColor: "white",
            };
            break;
          case "ajuste":
            config = {
              label: "Ajuste",
              icon: <FaTools />,
              gradient: "linear(to-r, orange.400, orange.600)",
              textColor: "white",
            };
            break;
        }

        return (
          <Box
            bgGradient={config.gradient}
            color={config.textColor}
            px={3}
            py={1}
            borderRadius="full"
            fontSize="xs"
            display="inline-flex"
            alignItems="center"
            gap={1}
            fontWeight="medium"
            boxShadow="sm"
          >
            {config.icon}
            {config.label}
          </Box>
        );
      },
    }),
    columnHelper.accessor("cantidad", {
      header: () => <span className="font-bold">Cantidad</span>,
      cell: info => <span>{info.getValue()}</span>,
    }),
    columnHelper.accessor("precioUnitario", {
      header: () => <span className="font-bold">Precio Unit</span>,
      cell: info => <span className="font-semibold">{currencyFormat(info.getValue())}</span>,
    }),
    columnHelper.accessor("stockAntes", {
      header: () => <span className="font-bold">Stock antes</span>,
      cell: info => <span>{info.getValue()}</span>,
    }),
    columnHelper.accessor("stockDespues", {
      header: () => <span className="font-bold">Stock despues</span>,
      cell: info => <span>{info.getValue()}</span>,
    }),
    columnHelper.accessor("fecha", {
      header: () => <span className="font-bold">Fecha de registro</span>,
      cell: info => <span>{moment(info.getValue()).format('DD/MM/YYYY hh:mm a')}</span>,
    })
  ], []);

  const table = useReactTable({
    data: tableData,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    pageCount,
    debugTable: false,
  });

  useEffect(() => {
    const { pageIndex, pageSize } = table.getState().pagination ?? { pageIndex: 0, pageSize: 10 };
    fetchData(pageSize, pageIndex, searchTerm, sorting);
  }, [fetchData, searchTerm, sorting, table.getState().pagination]);

  return (
    <Card extra="w-full h-full overflow-auto p-4">
      <div className="overflow-x-auto">
        <Box className="w-full overflow-x-auto rounded-md">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-gray-300/50 dark:bg-navy-600/50">
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <th
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler()}
                      className={`p-3 cursor-pointer font-semibold ${header.column.columnDef.meta?.headerClass || ''}`}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.length > 0 ? (
                table.getRowModel().rows.map(row => (
                  <tr key={row.id} className="border-t border-gray-300/50 dark:border-navy-600/50 hover:bg-gray-300/30 
                  dark:hover:bg-navy-600/30 transition-colors duration-500 ease-in-out">
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id} className={`p-3 ${cell.column.columnDef.meta?.headerClass || ''}`}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={table.getAllColumns().length}>
                    <Box
                      py={10}
                      textAlign="center"
                      className="text-center flex flex-col items-center justify-center text-gray-500 dark:text-gray-400"
                    >
                      <Icon as={FiInbox} boxSize={10} className="mb-2" />
                      <Text fontSize="md" fontWeight="medium">
                        No se encontraron resultados
                      </Text>
                    </Box>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Box>
        {loading && <Loader color="#64707d" className="mt-4" />}
      </div>

      <PaginationControls table={table} totalRow={totalRow} />

    </Card>
  );
}
